import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Loader2, ShieldCheck, FileText, DollarSign, MessageSquare, ScrollText, UserCog } from "lucide-react";
import api from "@/lib/api";
import { StatusBadge } from "./NetworkCommand";
import { ChangeRequestCard } from "./ChangeRequestsPanel";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const fmt = (s) => (s ? new Date(s).toLocaleString() : "—");

const ACTIONS = [
  { key: "approve", label: "Approve", tone: "bg-emerald-600 hover:bg-emerald-700" },
  { key: "deny", label: "Deny", tone: "bg-red-600 hover:bg-red-700", reason: true, message: true },
  { key: "suspend", label: "Suspend", tone: "bg-orange-600 hover:bg-orange-700", reason: true },
  { key: "reactivate", label: "Reactivate", tone: "bg-emerald-600 hover:bg-emerald-700" },
  { key: "deactivate", label: "Deactivate", tone: "bg-slate-600 hover:bg-slate-700", reason: true },
  { key: "archive", label: "Archive", tone: "bg-slate-500 hover:bg-slate-600", reason: true },
  { key: "request_info", label: "Request Info", tone: "bg-cobalt hover:bg-cobalt/90" },
  { key: "request_documents", label: "Request Documents", tone: "bg-cobalt hover:bg-cobalt/90" },
  { key: "request_license_renewal", label: "Request License Renewal", tone: "bg-cobalt hover:bg-cobalt/90" },
  { key: "reset_password", label: "Reset Password", tone: "bg-navy hover:bg-navy/90" },
  { key: "disable_login", label: "Disable Login", tone: "bg-red-700 hover:bg-red-800" },
  { key: "enable_login", label: "Enable Login", tone: "bg-emerald-700 hover:bg-emerald-800" },
  { key: "send_message", label: "Send Message", tone: "bg-navy hover:bg-navy/90", message: true },
  { key: "add_note", label: "Add Internal Note", tone: "bg-slate-700 hover:bg-slate-800", notes: true },
  { key: "assign_reviewer", label: "Assign Reviewer", tone: "bg-navy hover:bg-navy/90", reviewer: true },
];

export default function ProfessionalProfile({ network, memberId, onClose, onChanged }) {
  const [p, setP] = useState(null);
  const [confirm, setConfirm] = useState(null); // action config
  const [reason, setReason] = useState("");
  const [message, setMessage] = useState("");
  const [notes, setNotes] = useState("");
  const [reviewer, setReviewer] = useState("");
  const [busy, setBusy] = useState(false);
  const [section, setSection] = useState("overview");

  const load = () => api.get(`/network-command/${network}/members/${memberId}`).then(({ data }) => setP(data)).catch(() => toast.error("Could not load profile"));
  useEffect(() => { load(); }, [network, memberId]);

  const run = async () => {
    if (!confirm) return;
    if (confirm.reason && !reason.trim()) { toast.error("Reason is required"); return; }
    if (confirm.notes && !notes.trim()) { toast.error("Note text is required"); return; }
    if (confirm.reviewer && !reviewer.trim()) { toast.error("Reviewer name is required"); return; }
    if (confirm.key === "send_message" && !message.trim()) { toast.error("Message is required"); return; }
    setBusy(true);
    try {
      await api.post(`/network-command/${network}/members/${memberId}/action`, {
        action: confirm.key, reason: reason || undefined, message: message || undefined,
        notes: notes || undefined, reviewer: reviewer || undefined,
      });
      toast.success(`${confirm.label} completed`);
      setConfirm(null); setReason(""); setMessage(""); setNotes(""); setReviewer("");
      load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    finally { setBusy(false); }
  };

  const s = p?.summary;

  return (
    <div className="fixed inset-0 z-[60] bg-navy/40 backdrop-blur-sm flex justify-end" onClick={onClose}>
      <div className="w-full max-w-3xl h-full bg-slate-50 overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="nc-profile-panel">
        {!p ? <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 text-cobalt animate-spin" /></div> : (
          <>
            <div className="sticky top-0 z-10 bg-white border-b border-slate-100 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-full bg-navy flex items-center justify-center text-white font-bold">
                  {(s.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
                </div>
                <div>
                  <div className="font-heading font-bold text-lg text-navy" data-testid="nc-profile-name">{s.name || "Unnamed"}</div>
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    {s.role_title} · <span className="font-mono">{memberId}</span> <StatusBadge status={s.status} />
                  </div>
                </div>
              </div>
              <button onClick={onClose} data-testid="nc-profile-close" className="p-2 rounded-full hover:bg-slate-100"><X className="w-5 h-5 text-slate-500" /></button>
            </div>

            <div className="px-6 py-3 bg-white border-b border-slate-100 flex flex-wrap gap-2">
              {ACTIONS.map((a) => (
                <button key={a.key} onClick={() => setConfirm(a)} data-testid={`nc-action-${a.key}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold text-white transition-all ${a.tone}`}>
                  {a.label}
                </button>
              ))}
            </div>

            <div className="px-6 pt-4 flex gap-2 overflow-x-auto">
              {[["overview", "Overview", ShieldCheck], ["documents", "Documents", FileText], ["financial", "Financial", DollarSign],
                ["communications", "Communications", MessageSquare], ["audit", "Audit Trail", ScrollText]].map(([k, l, I]) => (
                <button key={k} onClick={() => setSection(k)} data-testid={`nc-profile-tab-${k}`}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap ${section === k ? "bg-navy text-white" : "bg-white border border-slate-200 text-slate-600"}`}>
                  <I className="w-3.5 h-3.5" /> {l}
                </button>
              ))}
            </div>

            <div className="p-6 space-y-4">
              {section === "overview" && (
                <>
                  <MetricStrip m={p.metrics} />
                  {(p.change_requests || []).some((cr) => ["pending", "info_requested"].includes(cr.status)) && (
                    <Card title="Pending Change Requests — protected fields awaiting your approval">
                      <div className="space-y-3" data-testid="nc-profile-change-requests">
                        {p.change_requests.filter((cr) => ["pending", "info_requested"].includes(cr.status)).map((cr) => (
                          <ChangeRequestCard key={cr.request_id} cr={cr} onDecide={() => { load(); onChanged?.(); }} />
                        ))}
                      </div>
                    </Card>
                  )}
                  <KV title="Identity" data={p.identity} testid="nc-profile-identity" />
                  <KV title="Contact Information" data={p.contact} />
                  <KV title="Professional Information" data={p.professional} testid="nc-profile-professional" />
                </>
              )}
              {section === "documents" && (
                <Card title="Documents & Credentials">
                  {p.documents.length === 0 ? <Empty text="No documents on file." testid="nc-docs-empty" /> : (
                    <div className="space-y-2">
                      {p.documents.map((d) => (
                        <div key={d.type} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0" data-testid={`nc-doc-${d.type}`}>
                          <div className="flex items-center gap-2"><FileText className="w-4 h-4 text-cobalt" />
                            <div><div className="text-sm font-medium text-navy">{d.type.replace(/_/g, " ")}</div>
                              <div className="text-xs text-slate-400">{d.filename || d.content_type}</div></div>
                          </div>
                          <div className="text-xs text-slate-400">{fmt(d.uploaded_at)}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              )}
              {section === "financial" && (
                <Card title="Payout Ledger">
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="bg-slate-50 rounded-2xl p-4"><div className="font-heading text-xl font-extrabold text-emerald-600">{money(p.metrics.lifetime_earnings)}</div><div className="text-xs text-slate-500 uppercase tracking-wider mt-1">Lifetime Earnings</div></div>
                    <div className="bg-slate-50 rounded-2xl p-4"><div className="font-heading text-xl font-extrabold text-amber-600">{money(p.metrics.pending_payouts)}</div><div className="text-xs text-slate-500 uppercase tracking-wider mt-1">Pending Payouts</div></div>
                  </div>
                  {p.payouts.length === 0 ? <Empty text="No payouts recorded for this professional." /> : (
                    <div className="space-y-2">
                      {p.payouts.map((x) => (
                        <div key={x.payout_id} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
                          <div><div className="text-sm font-medium text-navy">{money(x.amount)} · {x.status}</div>
                            <div className="text-xs text-slate-400 font-mono">{x.payout_id}</div></div>
                          <div className="text-xs text-slate-400">{fmt(x.created_at)}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  <p className="text-xs text-slate-400 mt-4">Stripe Connect status: sandbox · 1099 automation &amp; tax reporting arrive in a future phase.</p>
                </Card>
              )}
              {section === "communications" && (
                <>
                  <Card title={`Internal Notes (${p.notes.length})`}>
                    {p.notes.length === 0 ? <Empty text="No internal notes yet." /> : p.notes.map((n) => (
                      <div key={n.note_id} className="py-2 border-b border-slate-50 last:border-0" data-testid={`nc-note-${n.note_id}`}>
                        <div className="text-sm text-slate-700">{n.note}</div>
                        <div className="text-xs text-slate-400 mt-0.5">{n.author_name} · {fmt(n.created_at)}</div>
                      </div>
                    ))}
                  </Card>
                  <Card title="Notification & Message History">
                    {p.communications.length === 0 ? <Empty text="No communications sent yet." /> : p.communications.map((c, i) => (
                      <div key={i} className="py-2 border-b border-slate-50 last:border-0">
                        <div className="text-sm font-medium text-navy">{c.title}</div>
                        <div className="text-xs text-slate-500">{c.body}</div>
                        <div className="text-xs text-slate-400 mt-0.5">{c.type} · {fmt(c.created_at)}</div>
                      </div>
                    ))}
                  </Card>
                </>
              )}
              {section === "audit" && (
                <Card title={`Admin Audit Trail (${p.audit.length})`}>
                  {p.audit.length === 0 ? <Empty text="No admin actions recorded yet." testid="nc-audit-empty" /> : p.audit.map((a) => (
                    <div key={a.audit_id} className="py-2 border-b border-slate-50 last:border-0" data-testid={`nc-audit-${a.audit_id}`}>
                      <div className="text-sm font-semibold text-navy uppercase tracking-wide text-xs">{a.action.replace(/_/g, " ")}</div>
                      {a.reason && <div className="text-xs text-slate-600">Reason: {a.reason}</div>}
                      {a.message && <div className="text-xs text-slate-600">Message: {a.message}</div>}
                      <div className="text-xs text-slate-400 mt-0.5">by {a.actor_name} · {fmt(a.created_at)}</div>
                    </div>
                  ))}
                </Card>
              )}
            </div>
          </>
        )}
      </div>

      {confirm && (
        <div className="fixed inset-0 z-[70] bg-navy/50 flex items-center justify-center p-4" onClick={() => setConfirm(null)}>
          <div className="bg-white rounded-3xl p-6 w-full max-w-md shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="nc-confirm-dialog">
            <div className="flex items-center gap-2"><UserCog className="w-5 h-5 text-cobalt" />
              <h3 className="font-heading font-bold text-navy">Confirm: {confirm.label}</h3></div>
            <p className="text-sm text-slate-500 mt-2">This action will be permanently recorded in the audit trail{s?.name ? ` for ${s.name}` : ""}.</p>
            {confirm.reason && (
              <textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (required)" data-testid="nc-confirm-reason"
                className="mt-3 w-full px-3 py-2 rounded-xl border border-slate-200 text-sm" rows={2} />
            )}
            {confirm.message && (
              <textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder={confirm.key === "deny" ? "Optional message to the applicant" : "Message"} data-testid="nc-confirm-message"
                className="mt-3 w-full px-3 py-2 rounded-xl border border-slate-200 text-sm" rows={2} />
            )}
            {confirm.notes && (
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Internal note (required)" data-testid="nc-confirm-notes"
                className="mt-3 w-full px-3 py-2 rounded-xl border border-slate-200 text-sm" rows={2} />
            )}
            {confirm.reviewer && (
              <input value={reviewer} onChange={(e) => setReviewer(e.target.value)} placeholder="Reviewer name (required)" data-testid="nc-confirm-reviewer"
                className="mt-3 w-full px-3 py-2 rounded-xl border border-slate-200 text-sm" />
            )}
            {confirm.key === "deny" && (
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Internal notes (optional)" data-testid="nc-deny-notes"
                className="mt-3 w-full px-3 py-2 rounded-xl border border-slate-200 text-sm" rows={2} />
            )}
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setConfirm(null)} data-testid="nc-confirm-cancel"
                className="px-4 py-2 rounded-full text-sm font-medium text-slate-600 hover:bg-slate-50 border border-slate-200">Cancel</button>
              <button onClick={run} disabled={busy} data-testid="nc-confirm-execute"
                className={`px-4 py-2 rounded-full text-sm font-semibold text-white disabled:opacity-50 ${confirm.tone}`}>
                {busy ? "Working…" : `Confirm ${confirm.label}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Card = ({ title, children }) => (
  <div className="bg-white rounded-3xl border border-slate-100 p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
    <h4 className="font-heading font-semibold text-navy text-sm">{title}</h4>
    <div className="mt-3">{children}</div>
  </div>
);

const Empty = ({ text, testid }) => <div className="py-6 text-center text-sm text-slate-400" data-testid={testid}>{text}</div>;

function KV({ title, data, testid }) {
  const entries = Object.entries(data || {}).filter(([, v]) => v !== null && v !== undefined && v !== "");
  return (
    <Card title={title}>
      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2" data-testid={testid}>
        {entries.map(([k, v]) => (
          <div key={k} className="flex justify-between gap-3 py-1 border-b border-slate-50">
            <span className="text-xs text-slate-500">{k}</span>
            <span className="text-xs font-medium text-navy text-right">{typeof v === "boolean" ? (v ? "Yes" : "No") : String(v)}</span>
          </div>
        ))}
        {entries.length === 0 && <div className="text-sm text-slate-400">No data on file.</div>}
      </div>
    </Card>
  );
}

function MetricStrip({ m }) {
  const items = [
    ["Active Cases", m.current_active], ["Completed", m.completed],
    ["Rating", m.rating ?? "—"], ["Availability", m.availability ?? "—"],
    ["Lifetime Earnings", money(m.lifetime_earnings)], ["Pending Payouts", money(m.pending_payouts)],
  ];
  return (
    <div className="grid grid-cols-3 sm:grid-cols-6 gap-2" data-testid="nc-profile-metrics">
      {items.map(([l, v]) => (
        <div key={l} className="bg-white rounded-2xl border border-slate-100 p-3 text-center">
          <div className="font-heading text-sm font-extrabold text-navy">{v}</div>
          <div className="text-[9px] font-medium text-slate-400 uppercase tracking-wider mt-0.5">{l}</div>
        </div>
      ))}
      {m.login_disabled && <div className="col-span-full text-xs font-semibold text-red-600 bg-red-50 rounded-xl px-3 py-2" data-testid="nc-login-disabled-flag">⚠ Login is currently DISABLED for this professional</div>}
    </div>
  );
}
